import { useStore } from '@nanostores/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { IconButton } from '~/components/ui/IconButton';
import { workbenchStore } from '~/lib/stores/workbench';
import { PortDropdown } from '~/components/workbench/PortDropdown';
import { HeaderActionButtons } from '~/components/header/HeaderActionButtons.client';
import Logo from '~/assets/alpha-logo.png';
import { CopyToClipboardButton } from '~/components/ui/CopyToClipboardButton'; // Add this import
import type { PreviewError } from '~/lib/stores/previews';
import { ERROR_LIST, getCombinedErrorTextForCopy, groupAndCountErrors, type GroupedError } from '~/utils/errorUtils'; // Or wherever you place it

interface PreviewProps {
  isStreaming?: boolean;
}

export const Preview = memo(({ isStreaming = false }: PreviewProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [isPortDropdownOpen, setIsPortDropdownOpen] = useState(false);
  const hasSelectedPreview = useRef(false);
  const previews = useStore(workbenchStore.previews);
  const previewErrorArray = useStore(workbenchStore.previewError); // This is already an array
  const activePreview = previews[activePreviewIndex];

  console.info('Preview Error Array:', previewErrorArray);

  const [url, setUrl] = useState('');
  const [iframeUrl, setIframeUrl] = useState<string | undefined>();

  useEffect(() => {
    if (!activePreview) {
      setUrl('');
      setIframeUrl(undefined);

      return;
    }

    const { baseUrl } = activePreview;

    setUrl(baseUrl);
    setIframeUrl(baseUrl);
  }, [activePreview, iframeUrl]);

  const validateUrl = useCallback(
    (value: string) => {
      if (!activePreview) {
        return false;
      }

      const { baseUrl } = activePreview;

      if (value === baseUrl) {
        return true;
      } else if (value.startsWith(baseUrl)) {
        return ['/', '?', '#'].includes(value.charAt(baseUrl.length));
      }

      return false;
    },
    [activePreview],
  );

  const findMinPortIndex = useCallback(
    (minIndex: number, preview: { port: number }, index: number, array: { port: number }[]) => {
      return preview.port < array[minIndex].port ? index : minIndex;
    },
    [],
  );

  // when previews change, display the lowest port if user hasn't selected a preview
  useEffect(() => {
    if (previews.length > 1 && !hasSelectedPreview.current) {
      const minPortIndex = previews.reduce(findMinPortIndex, 0);

      setActivePreviewIndex(minPortIndex);
    }
  }, [previews]);

  const reloadPreview = () => {
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  const groupedErrors = groupAndCountErrors(previewErrorArray);
  const totalOriginalErrors = previewErrorArray?.length || 0;

  const combinedText = getCombinedErrorTextForCopy(groupedErrors);

  const errorType = ERROR_LIST.find((error) => combinedText.includes(error)) || 'Preview Error';

  return (
    <div className="w-full h-full flex flex-col">
      {isPortDropdownOpen && (
        <div className="z-iframe-overlay w-full h-full absolute" onClick={() => setIsPortDropdownOpen(false)} />
      )}
      <div className="bg-alpha-elements-background-depth-2 p-2 flex items-center gap-1.5">
        <IconButton icon="i-ph:arrow-clockwise" onClick={reloadPreview} />
        <div
          className="flex items-center gap-1 flex-grow bg-alpha-elements-preview-addressBar-background  text-alpha-elements-preview-addressBar-text rounded-full px-3 py-1 text-sm 
        "
        >
          {/* border border-alpha-elements-borderColor focus-within-border-alpha-elements-borderColorActive focus-within:text-alpha-elements-preview-addressBar-textActive hover:bg-alpha-elements-preview-addressBar-backgroundHover hover:focus-within:bg-alpha-elements-preview-addressBar-backgroundActive focus-within:bg-alpha-elements-preview-addressBar-backgroundActive*/}
          {/* <input
            ref={inputRef}
            className="w-full bg-transparent outline-none"
            type="text"
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && validateUrl(url)) {
                setIframeUrl(url);

                if (inputRef.current) {
                  inputRef.current.blur();
                }
              }
            }}
          /> */}
        </div>
        <HeaderActionButtons />

        {previews.length > 1 && (
          <PortDropdown
            activePreviewIndex={activePreviewIndex}
            setActivePreviewIndex={setActivePreviewIndex}
            isDropdownOpen={isPortDropdownOpen}
            setHasSelectedPreview={(value) => (hasSelectedPreview.current = value)}
            setIsDropdownOpen={setIsPortDropdownOpen}
            previews={previews}
          />
        )}
      </div>
      <div className="flex-1 border-t border-alpha-elements-borderColor">
        {activePreview ? (
          groupedErrors.length > 0 ? (
            <div className="flex w-full h-full flex-col gap-4 justify-center items-center bg-alpha-background-secondary p-4 overflow-y-auto">
              <div className="flex flex-col gap-5">
                <h1 className="text-[25px] font-bold text-center text-gray-200">{errorType}</h1>
                <div className="flex flex-row gap-5 items-center justify-center relative group">
                  <p className="text-lg text-gray-200">
                    {totalOriginalErrors > 1
                      ? `${totalOriginalErrors} Uncaught Error${totalOriginalErrors > 1 ? 's' : ''}`
                      : 'Uncaught Error'}
                  </p>
                  <CopyToClipboardButton
                    code={combinedText}
                    className="bg-white opacity-100 group-hover:opacity-100 relative top-0 right-[-20px] rounded-md z-10 text-lg flex items-center justify-center"
                  />
                </div>
              </div>
            </div>
          ) : (
            <iframe ref={iframeRef} className="border-none w-full h-full bg-white" src={iframeUrl} />
          )
        ) : (
          <div className="flex w-full h-full flex-col justify-center items-center bg-gray-500">
            <img src={Logo} alt="Logo" className="mb-4 w-32 " />
            <p className="text-lg text-gray-200">No preview available</p>
          </div>
        )}
      </div>
    </div>
  );
});
